import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Link
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';

import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useDependencies, dependencyModules, targetTypes as dependencyTargetTypes } from '../../../hooks/api/useDependencies';

import { useTranslations } from '../../../hooks/useTranslations';

export default function ListPurchaseInvoices({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useDocuments({ documentType: documentTypes.purchaseInvoice });
    const { RenderDatagridDisplay: RenderDependencyDatagridDisplay } = useDependencies()

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getLocationIds } = useAppContextFeatures();

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.purchaseInvoice.id)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    useEffect(() => {

        setRefreshDataGrid(val => val + 1)

    }, [getLocationIds])

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {
            props.dataTypes = ["dependency"]

            props.customFilterLocationEnable = true;
            props.customFilterLocationFields = ['location_id']

            const result = await list(props)

            let rawData = result?.data?.documents

            let newData = []
            
            rawData.forEach(i => {

                let _i = i

                let outward = _i?.outward_dependencies
                let inward = _i?.inward_dependencies

                let all = [...outward, ...inward]

                // declare a value and allocate the combined data
                _i.all_dependencies = all

                newData.push(_i)
            })

            return {
                data: newData,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'location_name', header: t('location')
            },
            {
                name: 'doc_number',
                header: t('docNo'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`documents/purchaseInvoice/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'doc_date', header: t('date')
            },
            {
                name: 'doc_remark', header: t('remark')
            },
            {
                name: 'profile_name', header: t('supplier')
            },
            {
                name: 'total_amount', header: t('amount')
            },
            {
                name: 'is_paid_status',
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t("received"), t("pending")], ["primary", "default"], "")
            },
            {
                name: 'all_dependencies', 
                header: t('relatedDocs'),
                renderValue: ({ value, cellProps }) => {

                    return (
                        <RenderDependencyDatagridDisplay
                            id={cellProps.data.id}
                            values={value}
                            renderValue={({ id, otherUrl }) => {

                                if(otherUrl){
                                    return getRoute(otherUrl)
                                }
    
                                return getRoute(`documents/purchaseInvoice/view/${id}`)
                            }}
                        />
                    )

                }

            },
        ])
    }

    const filterValue =
        [
            { name: 'doc_number', operator: 'eq', type: 'string', value: '' },
            { name: 'doc_date', operator: 'eq', type: 'string', value: '' },
            { name: 'doc_remark', operator: 'eq', type: 'string', value: '' },
            { name: 'profile_name', operator: 'eq', type: 'string', value: '' },
            { name: 'location_name', operator: 'eq', type: 'string', value: '' },
            { name: 'total_amount', operator: 'eq', type: 'string', value: '' },
            { name: 'is_paid_status', operator: 'eq', type: 'string', value: '' },
        ];

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.purchaseInvoice })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {

                        navigate(getRoute('documents/purchaseInvoice/new'));

                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValue,
                    onSelectionChange: (items) => {
                        // console.log('items selected : ', items)
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.purchaseInvoices })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("purchaseInvoices")}
                breadcrumbs={[
                    {
                        displayText: t("purchaseInvoices"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
